/**
 * MyCampaigns — Donation Chaser Dashboard
 *
 * Built on top of the per-campaign team scoping shipped in iteration 246.
 * Anyone who is a member of one-or-more `campaign_team_members` rows gets a
 * focused dashboard here:
 *   - Sidebar: their assigned campaigns + role badge.
 *   - Main view: pending donations they need to chase, with one-click
 *     "Mark Paid" and "Send Reminder" actions inline.
 *
 * Super admins see no campaigns here — they use the full /admin/campaigns
 * surface instead. This page is intentionally narrow.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuthStore } from '@/lib/store';
import afterLogin from '@/lib/afterLogin';
import {
  Heart, Mail, CheckCircle2, Clock, Search, ExternalLink,
  Loader2, AlertCircle, Bell, Users, Banknote
} from 'lucide-react';

const ROLE_BADGE = {
  campaign_admin:   'bg-violet-100 text-violet-700 border-violet-200',
  campaign_manager: 'bg-blue-100 text-blue-700 border-blue-200',
  donation_chaser:  'bg-emerald-100 text-emerald-700 border-emerald-200',
  viewer:           'bg-slate-100 text-slate-700 border-slate-200',
};
const ROLE_LABEL = {
  campaign_admin: 'Admin',
  campaign_manager: 'Manager',
  donation_chaser: 'Chaser',
  viewer: 'Viewer',
};

const STATUS_BADGE = {
  pending:    'bg-amber-100 text-amber-700',
  completed:  'bg-emerald-100 text-emerald-700',
  failed:     'bg-red-100 text-red-700',
  refunded:   'bg-slate-100 text-slate-700',
};

const MyCampaigns = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isAuthed = useAuthStore((s) => !!s.token);
  const logout = useAuthStore((s) => s.logout);
  const [activeId, setActiveId] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');

  // 1. Caller's scoped memberships
  const { data: memberships = [], isLoading: loadingMems } = useQuery({
    queryKey: ['my-campaign-memberships'],
    queryFn: () => api.get('/campaigns/team/mine').then(r => r.data),
  });

  // Default-select the first campaign once memberships load
  useEffect(() => {
    if (!activeId && memberships.length > 0) {
      setActiveId(memberships[0].campaign_id);
    }
  }, [memberships, activeId]);

  const activeMembership = memberships.find(m => m.campaign_id === activeId);
  const canChase = activeMembership && ['campaign_admin', 'campaign_manager', 'donation_chaser'].includes(activeMembership.role);

  // 2. Donations for the active campaign
  const { data: donations = [], isLoading: loadingDonations } = useQuery({
    queryKey: ['campaign-donations', activeId, statusFilter],
    queryFn: () => api.get(
      `/campaigns/admin/${activeId}/donations${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`
    ).then(r => r.data),
    enabled: !!activeId,
  });

  // 3. Mutations
  const markPaidMutation = useMutation({
    mutationFn: (donationId) => {
      const fd = new FormData();
      fd.append('status', 'completed');
      return api.put(
        `/campaigns/admin/${activeId}/donations/${donationId}/status`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
    },
    onSuccess: () => {
      toast.success('Pledge marked as paid');
      queryClient.invalidateQueries({ queryKey: ['campaign-donations', activeId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to update'),
  });

  const remindMutation = useMutation({
    mutationFn: (donationId) =>
      api.post(`/campaigns/admin/${activeId}/donations/${donationId}/remind`),
    onSuccess: (res) => {
      const d = res.data;
      if (d.admin_fallback) {
        toast.success(d.message || 'Team notified to follow up by phone (no donor email).');
      } else {
        toast.success(d.email_sent ? 'Reminder email sent' : 'Reminder logged (email not configured)');
      }
      queryClient.invalidateQueries({ queryKey: ['campaign-donations', activeId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to send reminder'),
  });

  const remindAllMutation = useMutation({
    mutationFn: () =>
      api.post(`/campaigns/admin/${activeId}/donations/remind-all-pending`),
    onSuccess: (res) => {
      const d = res.data;
      toast.success(
        `Reminders: ${d.emails_sent} email${d.emails_sent === 1 ? '' : 's'} sent, ` +
        `${d.admin_fallbacks} routed to team, ${d.skipped_recently_reminded} skipped (recent), ` +
        `${d.skipped_no_contact_route} no contact route.`
      );
      queryClient.invalidateQueries({ queryKey: ['campaign-donations', activeId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to send reminders'),
  });

  // Search filter (client-side on the loaded donations list)
  const filtered = useMemo(() => {
    if (!search.trim()) return donations;
    const q = search.toLowerCase();
    return donations.filter(d =>
      (d.donor_name || '').toLowerCase().includes(q) ||
      (d.donor_email || '').toLowerCase().includes(q) ||
      (d.reference || '').toLowerCase().includes(q)
    );
  }, [donations, search]);

  // Quick stats for the header
  const stats = useMemo(() => {
    const pending = donations.filter(d => d.status === 'pending');
    const completed = donations.filter(d => d.status === 'completed');
    const pendingTotal = pending.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
    const completedTotal = completed.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
    return {
      pendingCount: pending.length,
      completedCount: completed.length,
      pendingTotal,
      completedTotal,
    };
  }, [donations]);

  if (loadingMems) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (memberships.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 py-16">
        <div className="max-w-xl mx-auto px-4">
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-800 mb-2">No Campaign Assignments</h2>
              <p className="text-slate-500 text-sm mb-6">
                You haven't been added to any campaign team yet. A campaign admin needs to assign you a role
                (Chaser / Manager) before this dashboard becomes useful.
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Button
                  size="sm"
                  onClick={async () => {
                    try {
                      // Let the smart-landing service decide the right page
                      await afterLogin({ navigate, userType: 'admin', tenantId: null, redirect: '' });
                    } catch {
                      navigate('/admin', { replace: true });
                    }
                  }}
                  data-testid="my-campaigns-go-to-admin"
                >
                  Go to Admin Dashboard
                </Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => {
                    logout();
                    navigate('/campaigns/login', { replace: true });
                  }}
                  data-testid="my-campaigns-sign-out"
                >
                  Sign out
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="my-campaigns-page">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Heart className="h-6 w-6 text-emerald-600" />
            My Campaigns
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Pledges you're responsible for chasing. Mark paid, send reminders, follow up.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar — Campaigns */}
          <aside className="lg:col-span-1 space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
              Assigned ({memberships.length})
            </h3>
            {memberships.map(m => {
              const isActive = m.campaign_id === activeId;
              return (
                <button
                  key={m.id}
                  onClick={() => setActiveId(m.campaign_id)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    isActive
                      ? 'bg-white border-emerald-400 shadow-sm ring-1 ring-emerald-100'
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`my-campaigns-sidebar-${m.campaign_slug || m.campaign_id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className={`text-sm font-medium truncate ${isActive ? 'text-emerald-700' : 'text-slate-700'}`}>
                      {m.campaign_name || m.campaign_slug || 'Campaign'}
                    </p>
                  </div>
                  <span className={`inline-block text-xs px-1.5 py-0.5 rounded border ${ROLE_BADGE[m.role] || ROLE_BADGE.viewer}`}>
                    {ROLE_LABEL[m.role] || m.role}
                  </span>
                </button>
              );
            })}
          </aside>

          {/* Main area */}
          <main className="lg:col-span-3 space-y-4">
            {activeId && (
              <>
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 text-amber-600 mb-1">
                        <Clock className="h-4 w-4" />
                        <span className="text-xs uppercase tracking-wide font-medium">Pending</span>
                      </div>
                      <p className="text-2xl font-bold text-slate-900" data-testid="stat-pending-count">{stats.pendingCount}</p>
                      <p className="text-xs text-slate-500">${stats.pendingTotal.toLocaleString()} outstanding</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 text-emerald-600 mb-1">
                        <CheckCircle2 className="h-4 w-4" />
                        <span className="text-xs uppercase tracking-wide font-medium">Completed</span>
                      </div>
                      <p className="text-2xl font-bold text-slate-900" data-testid="stat-completed-count">{stats.completedCount}</p>
                      <p className="text-xs text-slate-500">${stats.completedTotal.toLocaleString()} received</p>
                    </CardContent>
                  </Card>
                  <Card className="col-span-2">
                    <CardContent className="p-4 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs uppercase tracking-wide font-medium text-slate-500 mb-1">Your role</p>
                        <p className="text-sm font-semibold text-slate-800">{ROLE_LABEL[activeMembership?.role] || activeMembership?.role}</p>
                      </div>
                      {activeMembership?.campaign_slug && (
                        <Link
                          to={`/campaigns/${activeMembership.campaign_slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-emerald-600 hover:underline inline-flex items-center gap-1"
                          data-testid="open-public-campaign"
                        >
                          View public page <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Filters */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center justify-between gap-3">
                      Pledges
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" />
                          <Input
                            placeholder="Search name, email, ref…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="h-8 pl-7 text-xs w-56"
                            data-testid="chaser-search"
                          />
                        </div>
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="text-xs border rounded h-8 px-2 bg-white"
                          data-testid="chaser-status-filter"
                        >
                          <option value="pending">Pending</option>
                          <option value="completed">Completed</option>
                          <option value="failed">Failed</option>
                          <option value="refunded">Refunded</option>
                          <option value="all">All</option>
                        </select>
                        {canChase && (
                          <Button
                            size="sm" variant="outline" className="h-8 text-xs"
                            disabled={remindAllMutation.isPending}
                            onClick={() => {
                              if (window.confirm('Send reminder to ALL pending pledges? Donors without email will route to the campaign team.')) {
                                remindAllMutation.mutate();
                              }
                            }}
                            data-testid="remind-all-pending-btn"
                            title="Send pledge reminders to all pending donors (skips anyone reminded in the last 24h)"
                          >
                            <Bell className="h-3.5 w-3.5 mr-1" />
                            {remindAllMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Remind All Pending'}
                          </Button>
                        )}
                        {canChase && (
                          <Button
                            size="sm" variant="outline" className="h-8 text-xs"
                            onClick={async () => {
                              try {
                                const res = await api.get(
                                  `/campaigns/admin/${activeId}/donations/export-missing-email`,
                                  { responseType: 'blob' }
                                );
                                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `missing-email-donors-${activeMembership?.campaign_slug || activeId}.csv`;
                                document.body.appendChild(a); a.click(); a.remove();
                                window.URL.revokeObjectURL(url);
                                toast.success('Export downloaded');
                              } catch (e) {
                                toast.error('Export failed');
                              }
                            }}
                            data-testid="export-missing-email-btn"
                            title="Download CSV of pledgers/donors that have no email — so you can call/SMS them"
                          >
                            <Users className="h-3.5 w-3.5 mr-1" /> Missing-Email CSV
                          </Button>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {loadingDonations ? (
                      <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
                    ) : filtered.length === 0 ? (
                      <div className="py-12 text-center text-slate-400 text-sm">
                        <AlertCircle className="h-6 w-6 mx-auto mb-2 opacity-50" />
                        No {statusFilter === 'all' ? '' : statusFilter} pledges
                      </div>
                    ) : (
                      <div className="divide-y">
                        {filtered.map(d => (
                          <div
                            key={d.id}
                            className="p-3 flex items-center gap-3 hover:bg-slate-50/50 transition-colors"
                            data-testid={`pledge-row-${d.id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-sm truncate">{d.donor_name || d.display_name || 'Anonymous'}</p>
                                <Badge variant="outline" className={`text-xs ${STATUS_BADGE[d.status] || STATUS_BADGE.pending}`}>
                                  {d.status}
                                </Badge>
                                <span className="text-xs font-mono text-slate-400">{d.reference}</span>
                              </div>
                              <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                                {d.donor_email && (
                                  <span className="inline-flex items-center gap-1 truncate">
                                    <Mail className="h-3 w-3" /> {d.donor_email}
                                  </span>
                                )}
                                {d.donor_phone && <span>{d.donor_phone}</span>}
                                <span className="capitalize">via {d.payment_method || '—'}</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-slate-900">
                                <Banknote className="h-3.5 w-3.5 inline-block mr-1 text-slate-400" />
                                ${parseFloat(d.amount).toLocaleString()}
                              </p>
                            </div>
                            {d.status === 'pending' && canChase && (
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm" variant="outline"
                                  className="h-8 px-2 text-xs"
                                  disabled={remindMutation.isPending}
                                  onClick={() => remindMutation.mutate(d.id)}
                                  data-testid={`pledge-remind-${d.id}`}
                                  title={!d.donor_email
                                    ? 'No email — campaign team will be notified to follow up by phone'
                                    : 'Send pledge reminder email'}
                                >
                                  <Bell className="h-3.5 w-3.5 mr-1" />
                                  {d.donor_email ? 'Remind' : 'Notify team'}
                                </Button>
                                <Button
                                  size="sm"
                                  className="h-8 px-2 text-xs bg-emerald-600 hover:bg-emerald-700"
                                  disabled={markPaidMutation.isPending}
                                  onClick={() => {
                                    if (window.confirm(`Mark $${d.amount} from ${d.donor_name} as paid?`)) {
                                      markPaidMutation.mutate(d.id);
                                    }
                                  }}
                                  data-testid={`pledge-mark-paid-${d.id}`}
                                >
                                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark Paid
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default MyCampaigns;
